import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageLayout, ActionButton, DataTable, MultiLanguageTextInput } from "../../../components/modern";
import { useAuth } from "../../../contexts/AuthContext";
import { adminApi } from "../../../utils/api";
import { showSuccessToast, showErrorToast } from "../../../utils/toastNotifications";

export const EditCurrency: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, i18n } = useTranslation('currencies');
  const isRTL = i18n.language === 'ar';

  const [formData, setFormData] = useState({
    name: "",
    symbol: "",
    id: id,
    hedge: 0,
  });
  const [loading, setLoading] = useState(false);
  const [reload, setReload] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [items, setItems] = useState<any[]>([]);


  const [exchangeFormData, setExchangeFormData] = useState({
    rate: 0.0,
    currencyId: id,
  });


  const [loadingExchange, setLoadingExchange] = useState(false);
  // Fetch existing item
  const fetchItem = async () => {
    console.log('in fetch items')
    try {
      const response = await adminApi.call(`/Currency/${id}`, 'GET');

      if (!response.success) {
        showErrorToast(response.error || "Failed to fetch item");
        return;
      }

      const data = response.data;
      setItems(data.exchangeRates);
      setFormData(data);
    } catch (error: any) {
      showErrorToast(t('messages.fetchError') + ": " + error.message);
    }
  };
  useEffect(() => {
    fetchItem();
  }, [id, reload]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: name === 'hedge' ? Number(value) : value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: {[key: string]: string} = {};
    if (!formData.name?.trim()) {
      newErrors.name = t('validation.nameRequired');
    } else if (formData.name.length < 2) {
      newErrors.name = t('validation.nameMinLength');
    }
    if (!formData.symbol?.trim()) {
      newErrors.symbol = t('validation.symbolRequired');
    }
    if (formData.hedge === null || formData.hedge === undefined || formData.hedge < 0) {
      newErrors.hedge = t('validation.hedgeRequired');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleExchangeInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setExchangeFormData({
      ...exchangeFormData,
      [name]: name === 'rate' ? Number(value) : value,
    });
  };
  const submitUpdate = async () => {
    if (!validateForm()) {
      showErrorToast(t('validation.fillAllFields'));
      return;
    }
    setLoading(true);
    setErrors({});

    try {
      const response = await adminApi.call('/Currency/admin/Update', 'PUT', formData);

      if (!response.success) {
        showErrorToast(response.error || t('messages.updateError'));
        return;
      }

      showSuccessToast(t('messages.updateSuccess'));

    } catch (error: any) {
      showErrorToast(t('messages.updateError') + ": " + error.message);
    } finally {
      setLoading(false);
    }
  };
const submitExchangeCreate = async () => {
  setLoadingExchange(true);

  try {
    const response = await adminApi.call('/exchangerate', 'POST', exchangeFormData);

    if (!response.success) {
      showErrorToast(response.error || t('messages.exchangeCreateError'));
      return;
    }

    showSuccessToast(t('messages.exchangeCreateSuccess'));
      fetchItem();

  } catch (error: any) {
    showErrorToast(t('messages.exchangeCreateError') + ": " + error.message);
  } finally {
    setLoadingExchange(false);
  }
};
  const breadcrumbs = [
    { label: t('breadcrumbs.currencies'), href: '/currencies' },
    { label: t('breadcrumbs.edit') }
  ];

  const exchangeColumns = [
    {
      key: 'date',
      title: t('exchangeRate.date'),
      sortable: true,
      render: (value: string) => {
        if (!value) return '-';
        const parsed = new Date(value);
        return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleString();
      }
    },
    {
      key: 'rate',
      title: t('exchangeRate.rate'),
      sortable: true,
      render: (value: number) => value.toFixed(4)
    },
    {
      key: 'source',
      title: 'Source',
      sortable: true,
      render: (value: string | number | null) => {
        if (value === 1 || value === 'API') return 'API';
        if (value === 0 || value === 'Manual') return 'Manual';
        return value ?? '-';
      }
    }
  ];

  return (
    <PageLayout
      title={t('editCurrency')}
      subtitle={t('subtitle')}
      breadcrumbs={breadcrumbs}

    >
      <div className="card shadow-lg border-0 rounded-3 p-4 mb-4">
        <h5 className={`card-title mb-3 ${isRTL ? 'text-end' : 'text-start'}`}>
          {t('currencyDetails')}
        </h5>
        <div className="row g-3">
          <div className="col-md-6">
            <MultiLanguageTextInput
              label={t('fields.name')}
              value={formData.name}
              onChange={(value) => {
                setFormData({ ...formData, name: value });
                if (errors.name) {
                  setErrors(prev => ({ ...prev, name: undefined }));
                }
              }}
              placeholder={t('placeholders.name')}
              error={errors.name}
              disabled={loading}
              required
            />
          </div>

          <div className="col-md-6">
            <MultiLanguageTextInput
              label={t('fields.symbol')}
              value={formData.symbol}
              onChange={(value) => {
                setFormData({ ...formData, symbol: value });
                if (errors.symbol) {
                  setErrors(prev => ({ ...prev, symbol: undefined }));
                }
              }}
              placeholder={t('placeholders.symbol')}
              error={errors.symbol}
              disabled={loading}
              required
            />
          </div>

          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('fields.hedge')} <span className="required">*</span>
            </label>
            <input
              type="number"
              className={`form-control ${isRTL ? 'text-end' : 'text-start'} ${errors.hedge ? 'is-invalid' : ''}`}
              name="hedge"
              step={0.001}
              value={formData.hedge}
              onChange={handleInputChange}
              placeholder={t('placeholders.hedge')}
              dir={isRTL ? 'rtl' : 'ltr'}
              min="0"
              required
            />
            {errors.hedge && <div className="invalid-feedback">{errors.hedge}</div>}
          </div>
        </div>

        <div className="text-center mt-4">
          <ActionButton
            variant="secondary"
            onClick={() => navigate('/currencies')}
            className={isRTL ? 'ms-2' : 'me-2'}
          >
            <i className={`fa ${isRTL ? 'fa-arrow-right' : 'fa-arrow-left'} ${isRTL ? 'ms-1' : 'me-1'}`}></i>
            {t('actions.back')}
          </ActionButton>
          <ActionButton
            variant="primary"
            onClick={submitUpdate}
            loading={loading}
            disabled={loading}
          >
            {loading ? t('actions.updating') : t('actions.update')}
          </ActionButton>
        </div>
      </div>

      <div className="card shadow-lg border-0 rounded-3 p-4 mb-4">
        <h5 className={`card-title mb-3 ${isRTL ? 'text-end' : 'text-start'}`}>
          {t('exchangeRate.addNew')}
        </h5>

        <div className="row g-3">
          <div className="col-md-6">
            <label className={`form-label ${isRTL ? 'text-end' : 'text-start'}`}>
              {t('exchangeRate.rate')}
            </label>
            <input
              type="number"
              step={0.01}
              className={`form-control ${isRTL ? 'text-end' : 'text-start'}`}
              name="rate"
              value={exchangeFormData.rate}
              onChange={handleExchangeInputChange}
              placeholder={t('exchangeRate.ratePlaceholder')}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </div>
        </div>

        <div className="text-center mt-3">
          <ActionButton
            variant="primary"
            onClick={submitExchangeCreate}
            loading={loadingExchange}
            disabled={loadingExchange}
          >
            {loadingExchange ? t('actions.creating') : t('actions.create')}
          </ActionButton>
        </div>
      </div>

      <div className="card shadow-lg border-0 rounded-3 p-4">
        <h5 className={`card-title mb-3 ${isRTL ? 'text-end' : 'text-start'}`}>
          {t('exchangeRate.history')}
        </h5>

        <DataTable
          columns={exchangeColumns}
          data={items}
          loading={false}
          searchable={false}
          filterable={false}
          exportable={true}
          refreshable={false}
          emptyMessage={t('exchangeRate.noRates')}

        />
      </div>
    </PageLayout>
  );
};
